import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./CheckpointGame.css";
import { selectAdminToken } from "../../admin/sessionSlice";
import {
  getCheckpointGame,
  requestCheckpointHint,
  submitCheckpointAnswer,
  submitCheckpointPhoto,
} from "../../functions/checkpointGame";
import { s3PublicUrl } from "../../utils/s3PublicUrl";
import Confetti from "../../components/Confetti/Confetti";
import { MEDIA_ANSWER_TYPES, MEDIA_ACCEPT } from "../../admin/pages/studio/constants";
import { sendReport } from "../../functions/sendReport";
import { Html5Qrcode } from "html5-qrcode";
import { badgeForRound, BadgeShape } from "../../components/BadgeShape/BadgeShape";

const QR_READER_ID = "cpg-qr-reader";

function readGameSettings() {
  try {
    return JSON.parse(sessionStorage.getItem("gameSettings") || "{}") || {};
  } catch {
    return {};
  }
}

function readUserData() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
  } catch {
    return {};
  }
}

function formatClock(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

// Live capture (photo/video/audio/gif) — same MediaRecorder approach as WarCryRecording.jsx.
// Max recording length per type; gif is capped short since it's uploaded as a short clip, not
// re-encoded into an actual animated gif (there's no gif encoder available client-side).
const CAPTURE_MAX_SECONDS = { video: 30, gif: 8, audio: 30 };

// MediaRecorder.isTypeSupported() only confirms the device can ENCODE a format — it says nothing
// about whether this device's own <video>/<audio> element can actually DECODE it back (see the
// identical comment in WarCryRecording.jsx, which this pattern is copied from).
function canPlaybackCapture(kind, type) {
  try {
    const el = document.createElement(kind === "audio" ? "audio" : "video");
    return !!el.canPlayType(type);
  } catch {
    return false;
  }
}

function pickCaptureMimeType(kind) {
  const candidates =
    kind === "audio"
      ? ["audio/webm", "audio/ogg", "audio/mp4"]
      : ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"];
  for (const type of candidates) {
    if (window.MediaRecorder && MediaRecorder.isTypeSupported?.(type) && canPlaybackCapture(kind, type)) return type;
  }
  return "";
}

/** The core hunt screen — one checkpoint/clue/answer at a time (see reference UI). */
function CheckpointGame() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((s) => s.auth?.user);
  const adminToken = useSelector(selectAdminToken);
  const { data } = useSelector((state) => state.theme);

  const headingColor = data?.headingColor || "#D83307";
  const textColor = data?.textColor || "#1a1a1a";
  const buttonBg = data?.buttonBgColor || "#D83307";
  const buttonText = data?.buttonTextColor || "#FFFFFF";
  const rawCover = data?.background_mob || data?.hunt_cover_image || data?.background_desk;
  const coverImage = rawCover ? s3PublicUrl(rawCover) : "";
  // The info-message popup specifically wants the desktop background (a fuller scene than the
  // mobile crop), falling back to whatever cover image is available if this theme never set one.
  const deskBgImage = data?.background_desk ? s3PublicUrl(data.background_desk) : coverImage;

  useEffect(() => {
    // "login-static-bg" locks html/body overflow:hidden so the fixed background never scrolls —
    // fine for a short login screen, but this page's content (task text, route plan card, media
    // capture frame, buttons) routinely runs taller than one viewport on a phone and was getting
    // silently clipped with no way to reach "Capture". The extra "cpg-body" class scopes a
    // scroll-enabling override (see CheckpointGame.css) back on for just this page.
    document.body.classList.add("login-static-bg", "cpg-body");
    if (coverImage) {
      document.body.style.backgroundImage = `url("${encodeURI(coverImage)}")`;
    }
    return () => {
      document.body.classList.remove("login-static-bg", "cpg-body");
      document.body.style.backgroundImage = "";
    };
  }, [coverImage]);

  const storedUserData = useMemo(readUserData, []);
  const gameSettings = useMemo(readGameSettings, []);
  const stageId = location.state?.stageId ?? gameSettings.stageId ?? null;
  const themeName = location.state?.themeName ?? gameSettings.themeName ?? "theme0";
  const uid = user?.userId ?? user?.userid ?? user?.id ?? storedUserData.userId ?? storedUserData.userid ?? storedUserData.id;

  const tokenForApi = useMemo(() => {
    const t = adminToken != null ? String(adminToken).trim() : "";
    if (t.includes("&")) return t;
    const sid = user?.sessionId ?? storedUserData.sessionId ?? "";
    const oid = user?.organizationId ?? storedUserData.organizationId ?? "";
    return sid && oid ? `${sid}&${oid}` : "";
  }, [adminToken, user, storedUserData]);

  const [loading, setLoading] = useState(true);
  const [game, setGame] = useState(null);
  const [answer, setAnswer] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [shake, setShake] = useState(false);
  const [elapsedNow, setElapsedNow] = useState(0);
  const baseElapsedRef = useRef(0);
  const fetchedAtRef = useRef(0);

  const [view, setView] = useState("game"); // 'game' | 'trail'
  const [needsClue, setNeedsClue] = useState(false);
  const [hintSheetOpen, setHintSheetOpen] = useState(false);
  const [stampResult, setStampResult] = useState(null); // set after a correct answer
  const [infoPopup, setInfoPopup] = useState(null); // { message, onOk } — shown before the stamp when the round has an info message
  const [confettiOn, setConfettiOn] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  // Some devices can encode a video/audio codec via MediaRecorder that their own <video>/<audio>
  // element then can't decode back (asymmetric hardware codec support — canPlaybackCapture() can
  // even say "yes" and still be wrong on a specific device) — that plays back as a silent,
  // permanently black/blank element with no indication anything is wrong. Surface it instead of
  // leaving a broken-looking player, since the recording itself is still fine to submit even if
  // this device can't preview it (see the identical handling in WarCryRecording.jsx).
  const [capturePreviewError, setCapturePreviewError] = useState(false);
  const [qrScanning, setQrScanning] = useState(false);
  const [qrError, setQrError] = useState("");
  // Holds the decoded QR value once a scan succeeds, before the round actually completes. A scan
  // alone used to submit (and complete the round) immediately, which was too eager — the checkpoint
  // is a link the team is meant to actually open, so completion now waits for them to tap it.
  const [qrDecoded, setQrDecoded] = useState("");
  const uploadInputRef = useRef(null);
  const qrInstanceRef = useRef(null);

  // Live camera/mic capture (photo/video/audio/gif) — opened by the "Capture" button instead of
  // the old hidden file input with a `capture` attribute, which many browsers (especially desktop)
  // silently ignore and fall back to a plain file picker.
  const [liveCapture, setLiveCapture] = useState(false);
  const [captureReady, setCaptureReady] = useState(false);
  const [captureRecording, setCaptureRecording] = useState(false);
  const [captureElapsedMs, setCaptureElapsedMs] = useState(0);
  const captureVideoElRef = useRef(null);
  const captureStreamRef = useRef(null);
  const captureLiveViewStreamRef = useRef(null);
  const captureRecorderRef = useRef(null);
  const captureChunksRef = useRef([]);
  const captureTimerRef = useRef(null);
  const captureAutoStopRef = useRef(null);
  const captureStoppingRef = useRef(false);

  const gameRef = useRef(null);
  useEffect(() => {
    gameRef.current = game;
  }, [game]);

  const submittingRef = useRef(false);
  useEffect(() => {
    submittingRef.current = submitting;
  }, [submitting]);

  // The round was already resolved server-side the instant this team's own correct submit
  // succeeded — before the info popup even opens, waiting on OK. A background poll that happens
  // to land in that window sees the round already advanced and, not knowing about the popup,
  // would fire its own stamp celebration and swap the whole page out from under it — closing the
  // popup on its own instead of on OK. Read via a ref (not the `infoPopup` state directly) so
  // load()'s stable useCallback identity doesn't need infoPopup in its dependency array.
  const infoPopupRef = useRef(null);
  useEffect(() => {
    infoPopupRef.current = infoPopup;
  }, [infoPopup]);

  // Set right before a load() call the player themselves triggered by tapping "Continue" past
  // their own correct answer — distinguishes that from a background poll discovering the round
  // advanced some other way (host approved a pending photo, or force-advanced with "Mark
  // complete"), which is the only case that should skip the normal reset and fire the
  // poll-driven stamp celebration below.
  const selfAdvancingRef = useRef(false);

  // Clears whatever the player had in progress for the round they were just on (typed answer,
  // picked MCQ option, chosen photo) and re-checks whether the new round needs its clue
  // acknowledged again — shared by both the normal fall-through below and the host-override poll
  // path, which skips straight to a new round without an interstitial in between.
  const resetInputStateForRound = (resp) => {
    setAnswer("");
    setSelectedOption(null);
    setPhotoPreview(null);
    setQrDecoded("");
    try {
      const ackRound = Number(sessionStorage.getItem(`cpg-clue-ack-${resp.stageId}`) || 0);
      setNeedsClue((resp.roundNumber || 0) > ackRound);
    } catch {
      setNeedsClue(true);
    }
  };

  const load = useCallback(async () => {
    if (!tokenForApi || (!stageId && !uid)) return;
    try {
      const prevGame = gameRef.current;
      const resp = await getCheckpointGame({ token: tokenForApi, stageId, userId: uid, themeName });

      // The info popup is up, waiting on OK — the round it's about to celebrate was already
      // resolved server-side the instant the answer was submitted, so this response would look
      // "advanced" too and, left unhandled, would swap the whole page to the stamp screen out
      // from under the popup instead of waiting for OK. Drop this response; the poll (or the OK
      // handler's own load()) picks the real state back up once the popup closes.
      if (infoPopupRef.current) return;

      // The hunt is finished — route to the finale page unconditionally, no matter how this
      // load() call happened: a fresh mount/reload landing here after the hunt already ended
      // (e.g. the last round's photo was approved while the tab was closed), a poll that just
      // discovered a pending review got approved as the final round, or anything else. The bare
      // "Hunt complete!" text below is legacy dead-end UI that never redirected on its own —
      // every path to `completed: true` must end up on /thankyou instead.
      if (resp.completed) {
        navigate("/thankyou", { replace: true, state: { totalScore: resp.totalScore, stageId } });
        return;
      }

      const wasPendingReview = !!prevGame?.pendingReview;
      const roundAdvanced = !!prevGame && (resp.roundNumber || 0) > (prevGame.roundNumber || 0);
      const isSelfAdvancing = selfAdvancingRef.current;
      selfAdvancingRef.current = false;

      // The round moved forward without this tab itself ever submitting a winning answer — either
      // a pending photo/video/audio got approved by the host, or the host force-advanced the round
      // with "Mark complete" in Live Host while the player hadn't submitted anything pending at
      // all. Both only ever surface here, via this background sync, never through handleSubmit's
      // own response — so the stamp-earned celebration has to fire from here too, exactly as if
      // they'd answered it themselves, instead of the content silently swapping underneath them.
      // The one exception is a host override ("Mark complete") — the team never actually got this
      // one right, so there's nothing to celebrate; just move them straight on to the next round.
      if (roundAdvanced && !resp.pendingReview && !isSelfAdvancing) {
        setGame(resp);
        baseElapsedRef.current = resp.elapsedSeconds || 0;
        fetchedAtRef.current = Date.now();
        if (resp.lastRoundWasHostOverride) {
          // Nothing to celebrate — the team never actually got this one right — so skip the stamp
          // straight to the new round's content, same as any other fresh round would reset to.
          resetInputStateForRound(resp);
          return;
        }
        setStampResult({
          points: (resp.totalScore || 0) - (prevGame.totalScore || 0),
          bonusPoints: resp.lastRoundBonusPoints || 0,
          hintUsed: !!resp.hintUsedPrevRound,
          isComplete: false,
          totalScore: resp.totalScore,
          answerSummary: "Approved",
          roundIndex: (prevGame.roundNumber || 1) - 1,
        });
        setConfettiOn(true);
        setTimeout(() => setConfettiOn(false), 2500);
        return;
      }

      if (wasPendingReview && !resp.pendingReview && !roundAdvanced) {
        Swal.fire({
          icon: "info",
          title: "Submission rejected",
          text: "The host asked you to try this round again.",
          confirmButtonColor: buttonBg,
        });
      }

      setGame(resp);
      baseElapsedRef.current = resp.elapsedSeconds || 0;
      fetchedAtRef.current = Date.now();

      // Only reset in-progress input state when the round actually changed (or this is the very
      // first load) — a background sync that finds nothing new must never wipe out an answer the
      // player is mid-typing, an MCQ option already picked, or a photo already chosen.
      const isNewRound = !prevGame || roundAdvanced || wasPendingReview !== !!resp.pendingReview;
      if (isNewRound) {
        resetInputStateForRound(resp);
      }
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to load your checkpoint", "error");
    } finally {
      setLoading(false);
    }
  }, [tokenForApi, stageId, uid, themeName]);

  useEffect(() => {
    load();
  }, [load]);

  // Client-side ticking clock between server syncs, re-based every time we fetch/submit.
  useEffect(() => {
    if (!game || game.completed || stampResult) return undefined;
    const tick = () => {
      const drift = (Date.now() - fetchedAtRef.current) / 1000;
      setElapsedNow(baseElapsedRef.current + drift);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [game, stampResult]);

  // Keep syncing with the server while the hunt is live, not just while a photo is awaiting host
  // review — the host can also force a round forward with "Mark complete" in Live Host at any
  // time, with no pending submission to poll for. Without this, that only reached the player on
  // their next manual reload. Skipped while actively submitting (to not yank state out from under
  // an in-flight request), once the stamp/finale screen is already showing, or while the info
  // popup is up waiting on OK (load() itself also guards this — see infoPopupRef — but there's no
  // point even polling while it can't do anything with the response).
  useEffect(() => {
    if (!game || stampResult || infoPopup) return undefined;
    const intervalMs = game.pendingReview ? 4000 : 6000;
    const id = setInterval(() => {
      if (!submittingRef.current) load();
    }, intervalMs);
    return () => clearInterval(id);
  }, [game?.pendingReview, !!game, stampResult, infoPopup, load]);

  const confirmHint = async () => {
    if (!game || game.hintUsedThisRound) return;
    try {
      const resp = await requestCheckpointHint({
        token: tokenForApi,
        stageId: game.stageId ?? stageId,
        roundIndex: (game.roundNumber ?? 1) - 1,
        themeName,
      });
      baseElapsedRef.current += (resp.hintPenaltyMin || 0) * 60;
      fetchedAtRef.current = Date.now();
      setGame((g) => ({ ...g, hintUsedThisRound: true }));
      setHintSheetOpen(false);
      await Swal.fire({
        icon: "info",
        title: "Hint",
        text: resp.hint || "No hint available for this checkpoint.",
        confirmButtonColor: buttonBg,
      });
    } catch (err) {
      setHintSheetOpen(false);
      Swal.fire("Error", err?.message || "Failed to load hint", "error");
    }
  };

  const handleBeginRound = () => {
    if (!game) return;
    try {
      sessionStorage.setItem(`cpg-clue-ack-${game.stageId}`, String(game.roundNumber));
    } catch {
      // sessionStorage unavailable — clue will just show again on refresh, non-fatal
    }
    setNeedsClue(false);
  };

  // Pushes this team's running score/time to the external report service, keyed by the reportId
  // seeded at join time (login.jsx / JoinLobby.jsx). Best-effort — never blocks gameplay.
  //
  // `gameSettings`/`storedUserData` are only read once at mount (useMemo with no deps) — but
  // sendReport() can self-heal a dead reportId (P2025, "no record found") by creating a fresh one
  // and writing it back to sessionStorage. Reusing those frozen snapshots meant every subsequent
  // call kept resending the SAME stale reportId forever, since the self-healed value never made
  // it back into this closure — hence the repeating 500s. Re-read sessionStorage fresh each call.
  const reportProgress = async (totalScore) => {
    const freshGameSettings = readGameSettings();
    const freshUserData = readUserData();
    // sendReport()'s self-heal (a dead reportId gets replaced with a freshly created one) only
    // ever writes the corrected id to sessionStorage's "userData" key, never "gameSettings" — so
    // that one has to win, or every call after a self-heal would just resend the same dead id.
    const reportId = freshUserData.reportId ?? freshGameSettings?.reportId;
    if (!reportId) return;
    try {
      await sendReport({
        sessionId: user?.sessionId ?? freshUserData.sessionId,
        organizationId: user?.organizationId ?? freshUserData.organizationId,
        role: "GUEST_USER",
        token: "",
        // Only ever read on the self-heal path (see sendReport.js's buildCreatePayload) — a stale/
        // deleted reportId gets recreated from scratch, and the create call needs these same
        // fields login.jsx originally seeded the report with, or the report service rejects (or
        // silently orphans) it. Missing here was why a self-heal after a dead reportId still
        // failed instead of recovering.
        name: user?.name ?? freshUserData.name ?? "",
        gameId: process.env.REACT_APP_EXTRAMILE_GAME_ID || "",
        userId: uid,
        reportId,
        points: totalScore,
        time: formatClock(elapsedNow),
      });
    } catch (e) {
      console.warn("[CheckpointGame] sendReport update failed:", e?.message || e);
    }
  };

  const showStampForCorrect = (resp, answerSummary) => {
    setStampResult({
      points: resp.pointsAwarded,
      hintUsed: resp.hintUsed,
      isComplete: resp.isComplete,
      totalScore: resp.totalScore,
      answerSummary,
      roundIndex: (game.roundNumber || 1) - 1,
    });
    setConfettiOn(true);
    setTimeout(() => setConfettiOn(false), 2500);
    reportProgress(resp.totalScore);
  };

  // If this checkpoint carries an info message, show it as a popup the team must acknowledge
  // right when they get it right — before the usual stamp-earned celebration, not instead of it —
  // since it's meant as a reveal/context moment tied to this specific answer, not just ambient
  // text they could've skipped past on the question screen.
  const onCorrect = (resp, answerSummary) => {
    const infoMessage = String(game?.infoMessage || "").trim();
    if (infoMessage) {
      setInfoPopup({ message: infoMessage, onOk: () => showStampForCorrect(resp, answerSummary) });
      return;
    }
    showStampForCorrect(resp, answerSummary);
  };

  // Shared submit path for anything validated as text (typed answer, numeric, or a scanned QR
  // value) — takes the value directly rather than reading `answer` state, so the QR scan-success
  // callback can submit immediately without waiting on a state update to land first.
  const submitPlainAnswer = async (value, summary) => {
    if (!game || submitting) return;
    setSubmitting(true);
    try {
      const resp = await submitCheckpointAnswer({
        token: tokenForApi,
        stageId: game.stageId ?? stageId,
        roundIndex: (game.roundNumber ?? 1) - 1,
        answer: value,
        themeName,
      });

      if (resp.stale || resp.pending) {
        await load();
        return;
      }
      if (!resp.correct) {
        setShake(true);
        setTimeout(() => setShake(false), 400);
        return;
      }
      onCorrect(resp, summary);
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to submit your answer", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const stopQrScanner = async () => {
    const instance = qrInstanceRef.current;
    qrInstanceRef.current = null;
    setQrScanning(false);
    if (instance) {
      try {
        await instance.stop();
        instance.clear();
      } catch {
        // already stopped — ignore
      }
    }
  };

  const startQrScanner = async () => {
    setQrError("");
    setQrDecoded("");
    setQrScanning(true);
    try {
      const instance = new Html5Qrcode(QR_READER_ID);
      qrInstanceRef.current = instance;
      await instance.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        async (decodedText) => {
          await stopQrScanner();
          // Just reveal what was scanned — completing the round now happens on openQrLink, when
          // the team actually taps to open it, not merely by pointing the camera at the code.
          setQrDecoded(decodedText.trim());
        },
        () => {
          // per-frame "no code found yet" callback — expected constantly while aiming, ignore
        }
      );
    } catch (err) {
      setQrScanning(false);
      setQrError("Couldn't access the camera. Check camera permissions and try again.");
    }
  };

  const isQrLink = /^https?:\/\//i.test(qrDecoded);

  // Opens the scanned link in a new tab (proving the team actually followed it, not just scanned
  // it) and, in the same tap, submits it as the answer — completing the round only now.
  const openQrLink = () => {
    if (isQrLink) window.open(qrDecoded, "_blank", "noopener,noreferrer");
    submitPlainAnswer(qrDecoded, "QR code scanned");
  };

  const rescanQr = () => {
    setQrDecoded("");
    startQrScanner();
  };

  // Stop the camera stream if the player navigates away mid-scan.
  useEffect(() => {
    return () => {
      if (qrInstanceRef.current) {
        qrInstanceRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!game || submitting) return;
    const isMcq = game.answerType === "mcq";
    const isInfo = game.answerType === "info";
    const isMedia = MEDIA_ANSWER_TYPES.includes(game.answerType);
    if (isMcq && selectedOption == null) return;
    if (!isMcq && !isMedia && !isInfo && !answer.trim()) return;
    if (isMedia && !photoPreview) return;

    if (isMedia) {
      setSubmitting(true);
      try {
        const resp = await submitCheckpointPhoto({
          token: tokenForApi,
          stageId: game.stageId ?? stageId,
          roundIndex: (game.roundNumber ?? 1) - 1,
          themeName,
          photoFile: photoPreview.file,
        });
        if (resp.stale || resp.pending) {
          await load();
          return;
        }
        // "Approve content" toggle is off — the host review step is skipped and this resolves
        // immediately, same shape as a correct text/MCQ answer.
        if (resp.correct) {
          onCorrect(resp, "Submitted");
        }
      } catch (err) {
        Swal.fire("Error", err?.message || "Failed to submit your file", "error");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (isMcq) {
      setSubmitting(true);
      try {
        const resp = await submitCheckpointAnswer({
          token: tokenForApi,
          stageId: game.stageId ?? stageId,
          roundIndex: (game.roundNumber ?? 1) - 1,
          selectedOption,
          themeName,
        });
        if (resp.stale || resp.pending) {
          await load();
          return;
        }
        if (!resp.correct) {
          setShake(true);
          setTimeout(() => setShake(false), 400);
          return;
        }
        onCorrect(resp, game.options?.[selectedOption]);
      } catch (err) {
        Swal.fire("Error", err?.message || "Failed to submit your answer", "error");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    await submitPlainAnswer(isInfo ? "acknowledged" : answer.trim(), isInfo ? "Acknowledged" : answer.trim());
  };

  const handleContinueFromStamp = async () => {
    const wasComplete = stampResult?.isComplete;
    const finalScore = stampResult?.totalScore;
    setStampResult(null);
    if (wasComplete) {
      navigate("/thankyou", { replace: true, state: { totalScore: finalScore, stageId } });
      return;
    }
    setLoading(true);
    selfAdvancingRef.current = true;
    await load();
    // Show the route plan first so the team has to check it to see where they're headed next —
    // checkpoint names are never shown on the clue/round screens themselves (see needsClue/game
    // render below), only here.
    setView("trail");
  };

  const onPickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCapturePreviewError(false);
    setPhotoPreview({ file, url: URL.createObjectURL(file) });
    e.target.value = "";
  };

  const handleCapturePreviewError = (ev) => {
    console.warn("[CheckpointGame] capture preview playback failed:", ev?.target?.error?.code, ev?.target?.error?.message);
    setCapturePreviewError(true);
  };

  const stopCaptureStream = () => {
    if (captureStreamRef.current) {
      captureStreamRef.current.getTracks().forEach((t) => t.stop());
      captureStreamRef.current = null;
    }
    if (captureLiveViewStreamRef.current) {
      captureLiveViewStreamRef.current.getTracks().forEach((t) => t.stop());
      captureLiveViewStreamRef.current = null;
    }
    setCaptureReady(false);
  };

  // Live preview stream — runs for photo/video/gif while the capture overlay is open and nothing
  // has been recorded yet. Audio requests its own stream lazily, only once recording starts (no
  // live preview needed for it).
  useEffect(() => {
    if (!liveCapture || !game || game.answerType === "audio") return undefined;
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: game.answerType === "video",
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        captureStreamRef.current = stream;
        // Never bind the exact same stream object to both the live <video> preview and
        // MediaRecorder — a real Chromium bug corrupts the recorded track when a MediaStream is
        // shared that way (see the identical comment in WarCryRecording.jsx).
        const liveViewStream = stream.clone();
        captureLiveViewStreamRef.current = liveViewStream;
        if (captureVideoElRef.current) captureVideoElRef.current.srcObject = liveViewStream;
        setCaptureReady(true);
      } catch {
        setCaptureReady(false);
        setLiveCapture(false);
        Swal.fire("Camera needed", "Couldn't access the camera. Check permissions and try again.", "error");
      }
    })();
    return () => {
      cancelled = true;
      stopCaptureStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveCapture, game?.answerType]);

  useEffect(() => () => stopCaptureStream(), []);

  const openLiveCapture = () => {
    captureChunksRef.current = [];
    setCaptureElapsedMs(0);
    setLiveCapture(true);
  };

  const closeLiveCapture = () => {
    if (captureTimerRef.current) clearInterval(captureTimerRef.current);
    if (captureAutoStopRef.current) clearTimeout(captureAutoStopRef.current);
    captureTimerRef.current = null;
    captureAutoStopRef.current = null;
    const recorder = captureRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.onstop = null; // closing without saving — discard whatever was recorded so far
      recorder.stop();
    }
    captureRecorderRef.current = null;
    captureChunksRef.current = [];
    captureStoppingRef.current = false;
    stopCaptureStream();
    setCaptureRecording(false);
    setCaptureElapsedMs(0);
    setLiveCapture(false);
  };

  // Photo: grab the current live-preview frame instead of recording anything. Uses toDataURL
  // (synchronous) rather than canvas.toBlob's async callback — that callback firing after
  // closeLiveCapture() had already torn down the stream/video element was leaving photoPreview
  // pointed at a blob URL that never actually resolved to image data, rendering as a broken
  // image. Capturing and setting the preview in one synchronous pass avoids that gap entirely.
  const snapPhoto = () => {
    const video = captureVideoElRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    const [, base64] = dataUrl.split(",");
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const file = new File([bytes], `checkpoint-photo-${Date.now()}.jpg`, { type: "image/jpeg" });
    setCapturePreviewError(false);
    setPhotoPreview({ file, url: dataUrl });
    closeLiveCapture();
  };

  const handleCaptureRecordingStopped = (kind) => {
    // The actual encoded bytes match whatever mimeType MediaRecorder was constructed with, NOT
    // chunksRef.current[0]?.type (browsers virtually always leave that empty) — mislabeling the
    // blob makes the browser try to decode it as the wrong codec (see WarCryRecording.jsx).
    const mimeType = captureRecorderRef.current?.mimeType || (kind === "audio" ? "audio/webm" : "video/webm");
    const blob = new Blob(captureChunksRef.current, { type: mimeType });
    captureChunksRef.current = [];
    if (blob.size === 0) return;
    const ext = mimeType.split("/")[1]?.split(";")[0] || "webm";
    const file = new File([blob], `checkpoint-${kind}-${Date.now()}.${ext}`, { type: mimeType });
    setCapturePreviewError(false);
    setPhotoPreview({ file, url: URL.createObjectURL(file) });
    closeLiveCapture();
  };

  const startCaptureRecording = async () => {
    if (captureRecording) return;
    const kind = game.answerType === "audio" ? "audio" : game.answerType; // video | gif | audio
    try {
      let stream = captureStreamRef.current;
      if (kind === "audio") {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        captureStreamRef.current = stream;
      }
      if (!stream) return; // video/gif stream not ready yet — capture button is disabled until it is

      const mimeType = pickCaptureMimeType(kind);
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      captureChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) captureChunksRef.current.push(e.data);
      };
      recorder.onstop = () => handleCaptureRecordingStopped(kind);
      captureRecorderRef.current = recorder;
      captureStoppingRef.current = false;
      recorder.start();
      setCaptureRecording(true);
      const startedAt = Date.now();
      captureTimerRef.current = setInterval(() => setCaptureElapsedMs(Date.now() - startedAt), 100);
      const maxSeconds = CAPTURE_MAX_SECONDS[kind] || 30;
      captureAutoStopRef.current = setTimeout(() => stopCaptureRecording(), maxSeconds * 1000);
    } catch (err) {
      Swal.fire("Camera/microphone needed", err?.message || "Couldn't access the camera or microphone.", "error");
    }
  };

  const stopCaptureRecording = () => {
    if (captureStoppingRef.current) return;
    captureStoppingRef.current = true;
    if (captureTimerRef.current) clearInterval(captureTimerRef.current);
    if (captureAutoStopRef.current) clearTimeout(captureAutoStopRef.current);
    setCaptureRecording(false);
    const recorder = captureRecorderRef.current;
    if (recorder && recorder.state !== "inactive") recorder.stop();
  };

  if (loading) {
    return (
      <div className="cpg-page">
        <div className="cpg-loading">Loading your checkpoint...</div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="cpg-page">
        <div className="cpg-loading">Unable to load the hunt. Please rejoin the lobby.</div>
      </div>
    );
  }

  if (game.pendingReview && !stampResult) {
    return (
      <div className="cpg-page">
        <div className="cpg-content cpg-stamp-content">
          <div className="cpg-checkpoint-icon cpg-pending-icon" style={{ borderColor: buttonBg, color: buttonBg }}>
            <i className="fa-solid fa-hourglass-half" />
          </div>
          <h1 className="cpg-stamp-title">Waiting for the host</h1>
          <p className="cpg-stamp-sub">
            Your submission is being reviewed. This screen will move on automatically.
          </p>
        </div>
      </div>
    );
  }

  // ── Stamp earned interstitial ─────────────────────────────────────────────────
  // No attempt limit — a round only ever resolves here by actually being answered correctly (or
  // the host force-completing it in Live Host), never by running out of tries, so this has no
  // "failed/round missed" state to render.
  if (stampResult) {
    const earnedBadge = badgeForRound(stampResult.roundIndex);
    return (
      <div className="cpg-page">
        <Confetti active={confettiOn} />
        <div className="cpg-content cpg-stamp-content">
          <div className="cpg-stamp-badge-shape">
            <BadgeShape shape={earnedBadge.shape} earned bg={earnedBadge.bg} fg={earnedBadge.fg} icon={earnedBadge.icon} />
          </div>
          <h1 className="cpg-stamp-title">Stamp earned</h1>
          <p className="cpg-stamp-sub">
            +{stampResult.points} points · {stampResult.hintUsed ? "hint used" : "no hints used"}
          </p>

          {stampResult.bonusPoints > 0 && (
            <div className="cpg-bonus-badge">🎉 +{stampResult.bonusPoints} bonus points from your host!</div>
          )}

          {!stampResult.isComplete && (
            <div className="cpg-next-card">
              <div className="cpg-checkpoint-label">NEXT CHECKPOINT</div>
              <div className="cpg-next-text">Check your Route Plan to see where to go next.</div>
            </div>
          )}

          <button
            type="button"
            className="cpg-submit"
            style={{ background: buttonBg, color: buttonText }}
            onClick={handleContinueFromStamp}
          >
            {stampResult.isComplete ? "Finish" : "Continue"}
          </button>
        </div>
      </div>
    );
  }

  // ── Trail / route overview ──────────────────────────────────────────────────
  if (view === "trail") {
    const stampedCount = (game.trail || []).filter((t) => t.status === "done").length;
    return (
      <div className="cpg-page">
        <div className="cpg-content">
          <div className="cpg-trail-header" onClick={() => setView("game")}>
            YOUR ROUTE
          </div>
          <h1 className="cpg-title" style={{ color: headingColor }}>
            {game.routeLabel} · {stampedCount} of {game.trail?.length || 0} stamped
          </h1>
          <ul className="cpg-trail-list">
            {(game.trail || []).map((cp, i) => (
              <li key={i} className="cpg-trail-row">
                <span
                  className={`cpg-trail-dot cpg-trail-dot--${cp.status}`}
                  style={cp.status !== "locked" ? { background: cp.status === "done" ? "#16a34a" : buttonBg } : undefined}
                >
                  {cp.status === "done" && <i className="fa-solid fa-check" />}
                </span>
                <span className={`cpg-trail-name cpg-trail-name--${cp.status}`}>
                  {cp.status === "done"
                    ? cp.name || `Checkpoint ${i + 1}`
                    : cp.status === "current"
                    ? "Be ready to reveal next round"
                    : "To be revealed"}
                </span>
              </li>
            ))}
          </ul>
          <p className="cpg-trail-footer">Upcoming checkpoints stay hidden until you complete the one you're on.</p>

          <button
            type="button"
            className="cpg-submit cpg-trail-continue"
            style={{ background: buttonBg, color: buttonText }}
            onClick={() => setView("game")}
          >
            Continue to next checkpoint
          </button>
        </div>
      </div>
    );
  }

  // ── Clue reveal page (shown once per round, before the answer form) ─────────
  if (needsClue) {
    return (
      <div className="cpg-page">
        <div className="cpg-content">
          <div className="cpg-header">
            <span className="cpg-team">
              TEAM {String(game.teamNumber || "").padStart(2, "0")} · {String(game.teamName || "").toUpperCase()}
            </span>
            <span className="cpg-clock" onClick={() => setView("trail")}>
              {game.roundNumber}/{game.totalRounds}
              {game.timeVisible !== false ? ` · ${formatClock(elapsedNow)}` : ""}
            </span>
          </div>

          <div className="cpg-progress-track">
            {Array.from({ length: game.totalRounds || 0 }).map((_, i) => {
              const currentIndex = game.roundNumber - 1;
              const style =
                i < currentIndex
                  ? { background: buttonBg }
                  : i === currentIndex
                  ? { background: buttonBg, opacity: 0.55 }
                  : undefined;
              return <span key={i} className="cpg-progress-seg" style={style} />;
            })}
          </div>

          <div className="cpg-checkpoint-card" onClick={() => setView("trail")}>
            <div className="cpg-checkpoint-icon" style={{ borderColor: buttonBg, color: buttonBg }}>
              <i className="fa-solid fa-map-pin" />
            </div>
            <div>
              <div className="cpg-checkpoint-label">CHECKPOINT</div>
              <div className="cpg-checkpoint-name">See your Route Plan</div>
            </div>
          </div>

          <div className="cpg-clue-eyebrow">YOUR CLUE</div>
          <p className="cpg-clue">{game.clue || game.question}</p>

          <button
            type="button"
            className="cpg-submit cpg-clue-cta"
            style={{ background: buttonBg, color: buttonText }}
            onClick={handleBeginRound}
          >
            I found it — answer now
          </button>
        </div>
      </div>
    );
  }

  // ── Main round screen ────────────────────────────────────────────────────────
  return (
    <div className="cpg-page">
      <div className="cpg-content">
        <div className="cpg-header">
          <span className="cpg-team">
            TEAM {String(game.teamNumber || "").padStart(2, "0")} · {String(game.teamName || "").toUpperCase()}
          </span>
          <span className="cpg-clock" onClick={() => setView("trail")}>
            {game.roundNumber}/{game.totalRounds} · {formatClock(elapsedNow)}
          </span>
        </div>

        <div className="cpg-progress-track">
          {Array.from({ length: game.totalRounds || 0 }).map((_, i) => {
            const currentIndex = game.roundNumber - 1;
            const style =
              i < currentIndex
                ? { background: buttonBg }
                : i === currentIndex
                ? { background: buttonBg, opacity: 0.55 }
                : undefined;
            return <span key={i} className="cpg-progress-seg" style={style} />;
          })}
        </div>

        <div className="cpg-checkpoint-card" onClick={() => setView("trail")}>
          <div className="cpg-checkpoint-icon" style={{ borderColor: buttonBg, color: buttonBg }}>
            <i className="fa-solid fa-map-pin" />
          </div>
          <div>
            <div className="cpg-checkpoint-label">CHECKPOINT</div>
            <div className="cpg-checkpoint-name">See your Route Plan</div>
          </div>
        </div>

        <div className="cpg-clue-eyebrow">YOUR TASK</div>
        <p className="cpg-clue">{game.question || game.clue}</p>

        <form className={`cpg-form${shake ? " cpg-shake" : ""}`} onSubmit={handleSubmit}>
          {game.answerType === "mcq" ? (
            <div className="cpg-options">
              {(game.options || []).map((opt, i) => (
                <button
                  type="button"
                  key={i}
                  className={`cpg-option${selectedOption === i ? " cpg-option--selected" : ""}`}
                  style={selectedOption === i ? { borderColor: buttonBg, color: buttonBg } : undefined}
                  onClick={() => setSelectedOption(i)}
                >
                  {opt}
                </button>
              ))}
            </div>
          ) : MEDIA_ANSWER_TYPES.includes(game.answerType) ? (
            <div className="cpg-photo-block">
              {game.answerType === "audio" ? (
                photoPreview ? (
                  <div className="cpg-audio-card">
                    <button
                      type="button"
                      className="cpg-photo-clear cpg-photo-clear--inline"
                      onClick={() => setPhotoPreview(null)}
                      aria-label="Remove and pick a different file"
                      title="Remove"
                    >
                      <i className="fa-solid fa-xmark" />
                    </button>
                    {capturePreviewError ? (
                      <div className="cpg-preview-fallback cpg-preview-fallback--audio">
                        <i className="fa-solid fa-triangle-exclamation" />
                        <span>Preview isn't available on this device, but your submission is ready to send.</span>
                      </div>
                    ) : (
                      <>
                        <i className="fa-solid fa-waveform-lines cpg-audio-icon" style={{ color: buttonBg }} />
                        <audio className="cpg-audio-player" src={photoPreview.url} controls onError={handleCapturePreviewError} />
                      </>
                    )}
                  </div>
                ) : (
                  <div className="cpg-audio-card cpg-audio-card--empty">
                    <i className="fa-solid fa-microphone cpg-audio-icon" style={{ color: buttonBg }} />
                    <span>No audio selected yet</span>
                  </div>
                )
              ) : (
                <div className="cpg-photo-frame">
                  {photoPreview ? (
                    <>
                      <button
                        type="button"
                        className="cpg-photo-clear"
                        onClick={() => setPhotoPreview(null)}
                        aria-label="Remove and pick a different file"
                        title="Remove"
                      >
                        <i className="fa-solid fa-xmark" />
                      </button>
                      {capturePreviewError ? (
                        <div className="cpg-preview-fallback">
                          <i className="fa-solid fa-triangle-exclamation" />
                          <span>Preview isn't available on this device, but your submission is ready to send.</span>
                        </div>
                      ) : // A captured "gif" is actually a short video clip (MediaRecorder has no gif
                      // encoder — see CAPTURE_MAX_SECONDS/handleCaptureRecordingStopped), so it needs a
                      // <video> element to play at all; an <img> can never render video bytes, which
                      // was a guaranteed broken preview for every gif captured live (not just an
                      // occasional codec mismatch). A real .gif picked via Upload is still a plain
                      // image, so key off the actual file type rather than the round's answer type.
                      game.answerType === "video" || photoPreview.file?.type?.startsWith("video/") ? (
                        <video
                          className="cpg-photo-preview"
                          src={photoPreview.url}
                          controls
                          playsInline
                          onError={handleCapturePreviewError}
                        />
                      ) : (
                        <img
                          src={photoPreview.url}
                          alt="Your submission"
                          className="cpg-photo-preview"
                          onError={handleCapturePreviewError}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <span className="cpg-photo-corner cpg-photo-corner--tl" style={{ borderColor: buttonBg }} />
                      <span className="cpg-photo-corner cpg-photo-corner--tr" style={{ borderColor: buttonBg }} />
                      <span className="cpg-photo-corner cpg-photo-corner--bl" style={{ borderColor: buttonBg }} />
                      <span className="cpg-photo-corner cpg-photo-corner--br" style={{ borderColor: buttonBg }} />
                    </>
                  )}
                </div>
              )}
              <input
                ref={uploadInputRef}
                type="file"
                accept={MEDIA_ACCEPT[game.answerType] || "image/*"}
                hidden
                onChange={onPickPhoto}
              />
              <div className="cpg-photo-actions">
                <button type="button" className="cpg-photo-btn cpg-photo-btn--upload" onClick={() => uploadInputRef.current?.click()}>
                  Upload
                </button>
                <button
                  type="button"
                  className="cpg-photo-btn cpg-photo-btn--capture"
                  style={{ background: buttonBg, color: buttonText }}
                  onClick={openLiveCapture}
                >
                  Capture
                </button>
              </div>

              {liveCapture && (
                <div className="cpg-capture-overlay">
                  <div className="cpg-capture-frame">
                    {game.answerType === "audio" ? (
                      <div className="cpg-capture-audio-indicator">
                        <i
                          className={`fa-solid fa-microphone cpg-audio-icon${captureRecording ? " cpg-capture-audio-icon--live" : ""}`}
                          style={{ color: buttonBg }}
                        />
                        <span>{captureRecording ? "Recording…" : "Ready to record"}</span>
                      </div>
                    ) : (
                      <video ref={captureVideoElRef} className="cpg-capture-video" autoPlay muted playsInline />
                    )}
                    {captureRecording && (
                      <span className="cpg-capture-rec-badge">
                        <span className="cpg-capture-rec-dot" /> REC {formatClock(captureElapsedMs / 1000)}
                      </span>
                    )}
                    {game.answerType !== "audio" && !captureReady && (
                      <div className="cpg-capture-placeholder">Waiting for camera…</div>
                    )}
                  </div>
                  <div className="cpg-capture-actions">
                    <button type="button" className="cpg-photo-btn" onClick={closeLiveCapture}>
                      Cancel
                    </button>
                    {game.answerType === "photo" ? (
                      <button
                        type="button"
                        className="cpg-photo-btn cpg-photo-btn--capture"
                        style={{ background: buttonBg, color: buttonText }}
                        disabled={!captureReady}
                        onClick={snapPhoto}
                      >
                        Snap
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="cpg-photo-btn cpg-photo-btn--capture"
                        style={{ background: buttonBg, color: buttonText }}
                        disabled={game.answerType !== "audio" && !captureReady}
                        onClick={captureRecording ? stopCaptureRecording : startCaptureRecording}
                      >
                        {captureRecording ? "Stop" : "Record"}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : game.answerType === "qrcode" ? (
            <div className="cpg-qr-block">
              {qrDecoded ? (
                <div className="cpg-qr-result">
                  <i className={`fa-solid ${isQrLink ? "fa-link" : "fa-qrcode"} cpg-qr-result-icon`} style={{ color: buttonBg }} />
                  <div className="cpg-qr-result-label">{isQrLink ? "Scanned link" : "Scanned code"}</div>
                  <div className="cpg-qr-result-value">{qrDecoded}</div>
                </div>
              ) : (
                <div className={`cpg-qr-frame${qrScanning ? " cpg-qr-frame--active" : ""}`}>
                  <div id={QR_READER_ID} className="cpg-qr-reader" />
                  {!qrScanning && (
                    <>
                      <span className="cpg-photo-corner cpg-photo-corner--tl" style={{ borderColor: buttonBg }} />
                      <span className="cpg-photo-corner cpg-photo-corner--tr" style={{ borderColor: buttonBg }} />
                      <span className="cpg-photo-corner cpg-photo-corner--bl" style={{ borderColor: buttonBg }} />
                      <span className="cpg-photo-corner cpg-photo-corner--br" style={{ borderColor: buttonBg }} />
                      <i className="fa-solid fa-qrcode cpg-qr-placeholder-icon" />
                    </>
                  )}
                </div>
              )}
              {qrError && <div className="cpg-attempts-warning">{qrError}</div>}
              {qrDecoded ? (
                <div className="cpg-photo-actions">
                  <button type="button" className="cpg-photo-btn" onClick={rescanQr} disabled={submitting}>
                    Scan again
                  </button>
                  <button
                    type="button"
                    className="cpg-photo-btn cpg-photo-btn--capture"
                    style={{ background: buttonBg, color: buttonText }}
                    onClick={openQrLink}
                    disabled={submitting}
                  >
                    {submitting ? "Checking..." : isQrLink ? "Open link" : "Confirm"}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="cpg-photo-btn cpg-photo-btn--capture"
                  style={{ background: buttonBg, color: buttonText, width: "100%" }}
                  onClick={qrScanning ? stopQrScanner : startQrScanner}
                  disabled={submitting}
                >
                  {qrScanning ? "Cancel scanning" : "Scan QR code"}
                </button>
              )}
            </div>
          ) : game.answerType === "info" ? null : (
            <label className="cpg-field">
              <span className="cpg-label">Your answer</span>
              <input
                className="cpg-input"
                type={game.answerType === "numeric" ? "number" : "text"}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Type your answer"
                autoFocus
              />
            </label>
          )}

          {game.answerType !== "qrcode" && (
            <button
              type="submit"
              className="cpg-submit"
              style={{ background: buttonBg, color: buttonText }}
              disabled={submitting}
            >
              {submitting ? "Checking..." : game.answerType === "info" ? "Continue" : "Submit"}
            </button>
          )}
        </form>

        <div className="cpg-footer">
          <button
            type="button"
            className="cpg-hint-link"
            onClick={() => setHintSheetOpen(true)}
            disabled={!game.hintAvailable || game.hintUsedThisRound}
          >
            <span className="cpg-hint-dot" style={{ background: buttonBg }} />
            {game.hintUsedThisRound
              ? "Hint used"
              : game.hintPenaltyType === "points"
              ? `Hint · costs ${game.hintPenaltyPoints} pts`
              : game.hintPenaltyType === "none"
              ? "Hint · free"
              : `Hint · costs ${game.hintPenaltyMin} min`}
          </button>
        </div>
      </div>

      {hintSheetOpen && (
        <div className="cpg-sheet-backdrop" onClick={() => setHintSheetOpen(false)}>
          <div className="cpg-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="cpg-sheet-eyebrow">REVEAL HINT?</div>
            <div className="cpg-sheet-title">
              {game.hintPenaltyType === "points"
                ? `${game.hintPenaltyPoints} point${game.hintPenaltyPoints === 1 ? "" : "s"} will be deducted from your team's score.`
                : game.hintPenaltyType === "none"
                ? "This hint is free — no time or points cost."
                : `${game.hintPenaltyMin} minute${game.hintPenaltyMin === 1 ? "" : "s"} will be added to your team's time.`}
            </div>
            <div className="cpg-sheet-warning">One hint per round. This cannot be undone once revealed.</div>
            <button type="button" className="cpg-sheet-reveal" onClick={confirmHint}>
              Reveal it
            </button>
            <button type="button" className="cpg-sheet-cancel" onClick={() => setHintSheetOpen(false)}>
              Keep trying
            </button>
          </div>
        </div>
      )}

      {infoPopup && (
        <div className="cpg-info-popup-backdrop">
          <div
            className="cpg-info-popup"
            style={deskBgImage ? { backgroundImage: `url("${encodeURI(deskBgImage)}")` } : undefined}
          >
            <div className="cpg-info-popup-inner">
              <p className="cpg-info-popup-text" style={{ color: textColor }}>
                {infoPopup.message}
              </p>
              <button
                type="button"
                className="cpg-info-popup-ok"
                style={{ background: buttonBg, color: buttonText }}
                onClick={() => {
                  const onOk = infoPopup.onOk;
                  setInfoPopup(null);
                  onOk();
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CheckpointGame;
